import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiShoppingBag, FiHeart, FiShield, FiExternalLink, FiLoader, FiZap } = FiIcons;

const Merch = () => {
  const [loading, setLoading] = useState(true);

  // Real products from Linda's Zazzle store
  const products = [
    {
      name: "LRCS Desk Mat",
      productUrl: "https://www.zazzle.com/lrcs_desk_mat-256976181523529112",
      imageUrl: "https://rlv.zcache.com/lrcs_desk_mat-r53eb576b850646038e463e112389cc55_ajohe4_644.webp?rlvnet=1",
      altText: "Linda Roberts Calls Scammers Desk Mat"
    },
    {
      name: "Futuristic LRCS Desk Mat",
      productUrl: "https://www.zazzle.com/futuristic_lrcs_desk_mat-256804692415603138",
      imageUrl: "https://rlv.zcache.com/futuristic_lrcs_desk_mat-rd5a825e1bba0417e882d8b5ab3004463_ajohe4_644.webp?rlvnet=1",
      altText: "Futuristic Desk Mat with Linda Roberts design"
    },
    {
      name: "Linda Roberts Calls Scammers Metal Heart Keychain",
      productUrl: "https://www.zazzle.com/linda_roberts_calls_scammers_metal_heart_keychain-256477437107952036",
      imageUrl: "https://rlv.zcache.com/linda_roberts_calls_scammers_metal_heart_keychain-r19b70a1b3ff940a09e35ef0343cebdcd_zfjxn_644.webp?rlvnet=1",
      altText: "Metal Heart Keychain with Linda Roberts Calls Scammers logo"
    },
    {
      name: "Linda Roberts Calls Scammers Tee – Scam Fighter",
      productUrl: "https://www.zazzle.com/linda_roberts_calls_scammers_tee_scam_fighter-256004502335657469",
      imageUrl: "https://rlv.zcache.com/linda_roberts_calls_scammers_tee_scam_fighter-r698672642a714683bdf1c637700e6760_k2gm8_644.webp",
      altText: "Scam Fighter T-shirt from Linda Roberts Calls Scammers"
    },
    {
      name: "Linda Roberts Calls Scammers Sexy Tote Bag",
      productUrl: "https://www.zazzle.com/linda_roberts_calls_scammers_sexy_tote_bag-256000831079356221",
      imageUrl: "https://rlv.zcache.com/linda_roberts_calls_scammers_sexy_tote_bag-r198d1a77198b445caabff6d5a9787f4e_6kcf1_644.webp?rlvnet=1",
      altText: "Stylish tote bag by Linda Roberts Calls Scammers"
    }
  ];

  useEffect(() => {
    // Simulate loading delay
    const timer = setTimeout(() => {
      setLoading(false);
    }, 800);

    return () => clearTimeout(timer);
  }, []);

  return (
    <section className="py-20 bg-gray-800">
      <div className="container mx-auto px-6">
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-pink-500 via-purple-500 to-cyan-400 bg-clip-text text-transparent drop-shadow-lg">
            Shop the Sass
          </h2>
          <p className="text-xl md:text-2xl text-gray-300 mb-6 font-medium">
            Scammer-blocking never looked so good. Grab Linda-approved merch that screams fierce, fabulous, and fraud-free.
          </p>
          <div className="flex items-center justify-center gap-2 text-gray-400">
            <SafeIcon icon={FiHeart} className="text-pink-500 animate-pulse" />
            <span className="font-medium">Official Linda Roberts merchandise</span>
            <SafeIcon icon={FiHeart} className="text-pink-500 animate-pulse" />
          </div>
        </motion.div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            >
              <SafeIcon icon={FiLoader} className="text-4xl text-pink-500" />
            </motion.div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              {products.map((product, index) => (
                <motion.div
                  key={index}
                  className="group cursor-pointer"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  viewport={{ once: true }}
                >
                  <div className="bg-gray-900 rounded-xl overflow-hidden shadow-lg hover:shadow-2xl hover:shadow-pink-500/20 transition-all duration-300 transform hover:scale-105 border border-gray-700 hover:border-pink-500/50 relative">
                    {/* Glitchy chrome effect overlay */}
                    <div className="absolute inset-0 bg-gradient-to-br from-pink-500/5 via-purple-500/5 to-cyan-400/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10 pointer-events-none"></div>
                    
                    {/* Product image */}
                    <div className="aspect-square bg-gradient-to-br from-gray-800 to-gray-900 relative overflow-hidden">
                      <img
                        src={product.imageUrl}
                        alt={product.altText}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      {/* Fallback icon */}
                      <div className="absolute inset-0 flex items-center justify-center bg-gray-800" style={{ display: 'none' }}>
                        <SafeIcon icon={FiShoppingBag} className="text-6xl text-white opacity-80" />
                      </div>
                      {/* Premium badge */}
                      <div className="absolute top-4 right-4 bg-gradient-to-r from-pink-500 to-purple-600 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                        OFFICIAL
                      </div>
                      {/* Hover glow effect */}
                      <div className="absolute inset-0 bg-gradient-to-t from-pink-500/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>
                    
                    {/* Product info */}
                    <div className="p-6 relative z-20">
                      <h3 className="font-bold text-white text-lg mb-4 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-pink-400 group-hover:to-cyan-400 group-hover:bg-clip-text transition-all duration-300 leading-tight">
                        {product.name}
                      </h3>
                      <a
                        href={product.productUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group/btn w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-400 hover:to-purple-500 text-white px-6 py-3 rounded-full font-bold text-sm transition-all duration-300 flex items-center justify-center gap-2 shadow-lg hover:shadow-pink-500/25 hover:scale-105 transform relative overflow-hidden"
                      >
                        {/* Button glow effect */}
                        <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                        <SafeIcon icon={FiZap} className="text-lg group-hover/btn:animate-pulse relative z-10" />
                        <span className="relative z-10">Buy Now</span>
                        <SafeIcon icon={FiExternalLink} className="text-sm relative z-10" />
                      </a>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="bg-gradient-to-br from-purple-900 to-gray-900 p-8 rounded-2xl border border-purple-500/30 max-w-2xl mx-auto mb-8 relative overflow-hidden">
                {/* Background decoration */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-pink-500/5 rounded-full -translate-y-16 translate-x-16"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-cyan-400/5 rounded-full translate-y-12 -translate-x-12"></div>
                
                <SafeIcon icon={FiShield} className="text-4xl text-cyan-400 mx-auto mb-4 relative z-10" />
                <h3 className="text-2xl font-bold text-white mb-4 relative z-10">
                  Support the Mission
                </h3>
                <p className="text-gray-300 leading-relaxed relative z-10">
                  Every purchase helps fund Linda's scam awareness education and supports the fight against online fraud. Wear your sass while making a difference!
                </p>
              </div>

              <a
                href="https://www.zazzle.com/store/lindarobertscs/products"
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-400 hover:to-purple-500 text-white px-10 py-5 rounded-full font-bold text-xl transition-all duration-300 transform hover:scale-105 inline-flex items-center gap-3 shadow-2xl hover:shadow-pink-500/30 relative overflow-hidden"
              >
                {/* Button chrome effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-white/10 via-transparent to-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <SafeIcon icon={FiExternalLink} className="text-2xl group-hover:rotate-12 transition-transform relative z-10" />
                <span className="relative z-10">View Full Store →</span>
              </a>
              
              <p className="text-gray-400 mt-4 text-sm">
                Shop official Linda Roberts merchandise on Zazzle
              </p>
            </motion.div>
          </>
        )}
      </div>
    </section>
  );
};

export default Merch;